/*
 * File: WarehouseLineItemSelect.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 18 May 2021 4:44:35 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { Form, Select, Spin, Tooltip } from 'antd';
import { useField } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetWarehousesQuery } from 'redux/services/useKingstonLivingstonService';
import { useDebouncedCallback } from 'use-debounce';

const styles: InlineStylesModel = {
  selectInput: {
    width: '100%'
  },
  formItem: {
    marginBottom: 0
  }
};

interface WarehouseLineItemSelectProps {
  warehouseId: SalesOrderPayloadModel['lines'][number]['warehouseId'];
  siteId: SalesOrderPayloadModel['lines'][number]['siteId'];
  id: SalesOrderPayloadModel['lines'][number]['id'];
  index: number;
}

export const WarehouseLineItemSelect = React.memo(({ warehouseId, index, id }: WarehouseLineItemSelectProps) => {
  const [x, _, { setValue }] = useField(`lines.${index}.warehouseId`);
  const [a, { value }, c] = useField(`lines.${index}.siteId`);
  const form = Form.useFormInstance();


  const siteValue = form.getFieldValue('siteId');

  const { siteId } = useSelector((state: ReduxState) => state.productsInfo);

  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);

  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  /* ****************** Hook API ****************** */
  const {
    data: warehouseResponse,
    isLoading,
    isFetching,
    isError
  } = useGetWarehousesQuery(
    {
      site: siteValue ?? '',
      offset,
      dataAreaId,
      warehouseContains: inputValue
    },
    { skip: !siteValue || !dataAreaId }
  );

  /* ****************** JSX ****************** */
  const optionsData = useMemo(() => warehouseResponse?.data.map((warehouse) => ({ label: warehouse.warehouseId, value: warehouse.warehouseId })), [warehouseResponse]);

  /* ****************** Functions ****************** */
  // handle delay of searching data
  const handleSearch = useDebouncedCallback((value: string) => {
    _setOffset(0);
    _setInputValue(value);
  }, 1000);

  const handleClear = (): void => {
    _setInputValue('');
    setValue('');
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;

    if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && warehouseResponse && warehouseResponse?.totalCount - offset > 25) {
      _setOffset((prevState) => prevState + 25);
    }
  };

  const handleSelect = (value: string): void => {
    form.setFieldValue('warehouseId', value);
    setValue(value);
    _setInputValue('');
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    return (
      <>
        {menu}
        {(isLoading || isFetching) && <Spin spinning />}
      </>
    );
  };

  const isInvalid = useMemo((): boolean => {
    if (warehouseId === '' && pathname.includes('/drafts/')) return true;

    return false;
  }, [pathname, warehouseId]);

  const errorMessage = useMemo((): string => {
    if (id.includes('import') && isError) return 'Unable to valid whorehouse on import';
    if (id.includes('import') && !warehouseId) return 'Required';
    if (warehouseId === '' && pathname.includes('/drafts/')) return 'Required';

    return 'Unable to report the issue';
  }, [isError, pathname, id, warehouseId]);

  /* ****************** Render ****************** */
  return (
    <Tooltip title={isInvalid ? errorMessage : undefined}>
      <Select
        allowClear
        disabled={!value || isPosted}
        dropdownRender={dropDownRender}
        filterOption={false}
        loading={isLoading || isFetching}
        onClear={handleClear}
        onPopupScroll={handleScroll}
        onSearch={handleSearch}
        onSelect={handleSelect}
        options={optionsData}
        value={form.getFieldValue('warehouseId')}
        showSearch
        style={styles.selectInput}
        status={isInvalid ? 'error' : undefined}
      />
    </Tooltip>
  );
});
